import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useSecuritySettings,
  useSecuritySettingsUpdate,
} from "@/queries/settings/use-security.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { AppDistribution } from "@/types/common.types.ts";
import { ApiSecuritySettings } from "@/types/security.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  Page,
  Skeleton,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToasts,
} from "@skailer-com/uikit";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseTenantIds(value: string): string[] {
  return value
    .split(",")
    .map((tenantId) => tenantId.trim().toLowerCase())
    .filter(Boolean);
}

function isTenantId(value: string): boolean {
  return TENANT_ID_PATTERN.test(value);
}

const DOMAIN_PATTERN = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

function parseDomains(value: string): string[] {
  return value
    .split(",")
    .map((domain) => domain.trim().toLowerCase().replace(/^@/, ""))
    .filter(Boolean);
}

function isDomain(value: string): boolean {
  return DOMAIN_PATTERN.test(value);
}

function parseNetworks(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((network) => network.trim())
    .filter(Boolean);
}

function parseScopes(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter(Boolean);
}

export function SecurityPage() {
  const { t } = useTranslation();
  const { data: settings, isLoading } = useSecuritySettings();
  const { showInfo } = useToasts();

  useTitle(t("core:settings_security_title"), t("common:settings"));

  const securitySchema = z.object({
    login_methods: z
      .object({
        password: z.object({
          enabled: z.boolean(),
        }),
        google: z.object({
          enabled: z.boolean(),
          hosted_domains: z.string().refine((value) => parseDomains(value).every(isDomain), {
            error: t("core:google_workspace_domains_invalid"),
          }),
        }),
        entra: z
          .object({
            enabled: z.boolean(),
            tenant_ids: z.string(),
          })
          .refine((value) => !value.enabled || parseTenantIds(value.tenant_ids).length > 0, {
            error: t("core:entra_sso_tenant_ids_required"),
            path: ["tenant_ids"],
          })
          .refine((value) => parseTenantIds(value.tenant_ids).every(isTenantId), {
            error: t("core:entra_sso_tenant_ids_invalid"),
            path: ["tenant_ids"],
          }),
        oidc: z
          .object({
            enabled: z.boolean(),
            title: z.string(),
            issuer: z.string(),
            client_id: z.string(),
            client_secret: z.string(),
            scopes: z.string(),
            email_claim: z.string(),
          })
          .refine((value) => !value.enabled || value.issuer.trim().startsWith("https://"), {
            error: t("core:oidc_issuer_invalid"),
            path: ["issuer"],
          })
          .refine((value) => !value.enabled || value.client_id.trim().length > 0, {
            error: t("errors:field_required"),
            path: ["client_id"],
          })
          .refine(
            (value) =>
              !value.enabled ||
              value.client_secret.trim().length > 0 ||
              settings?.login_methods.oidc.client_secret_set,
            { error: t("errors:field_required"), path: ["client_secret"] }
          )
          .refine(
            (value) =>
              !value.enabled ||
              ["openid", "email", "profile"].every((scope) =>
                parseScopes(value.scopes).includes(scope)
              ),
            { error: t("core:oidc_scopes_required"), path: ["scopes"] }
          ),
      })
      .refine(
        (value) =>
          value.password.enabled ||
          value.google.enabled ||
          value.entra.enabled ||
          value.oidc.enabled,
        t("core:login_methods_none_working")
      ),
    require_tfa: z.object({
      enabled: z.boolean(),
    }),
    ip_allowlist: z
      .object({
        enabled: z.boolean(),
        networks: z.string(),
      })
      .refine((value) => !value.enabled || parseNetworks(value.networks).length > 0, {
        error: t("core:ip_allowlist_networks_required"),
        path: ["networks"],
      }),
    allowed_domains: z.string().refine((value) => parseDomains(value).every(isDomain), {
      error: t("core:allowed_domains_invalid"),
    }),
    password_expiry_days: z.object({
      enabled: z.boolean(),
      value: z
        .number({ error: t("errors:field_required") })
        .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
        .max(180, t("api_errors:maximum_value_must", { maximum: 180 })),
    }),
    password_min_length: z.object({
      enabled: z.boolean(),
      value: z
        .number({ error: t("errors:field_required") })
        .min(8, t("api_errors:minimum_value_must", { minimum: 8 })),
    }),
  });

  type SecuritySchema = z.infer<typeof securitySchema>;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<SecuritySchema>({
    mode: "onSubmit",
    resolver: zodResolver(securitySchema),
  });

  const securitySettingsUpdate = useSecuritySettingsUpdate(control);

  const isGoogleEnabled = useWatch({ control, name: "login_methods.google.enabled" });
  const isEntraEnabled = useWatch({ control, name: "login_methods.entra.enabled" });
  const isEntraAvailable = import.meta.env.VITE_APP_DISTRIBUTION !== AppDistribution.headly;
  const isOidcEnabled = useWatch({ control, name: "login_methods.oidc.enabled" });
  const isIpAllowlistEnabled = useWatch({ control, name: "ip_allowlist.enabled" });

  useEffect(() => {
    if (settings) {
      reset({
        ...settings,
        login_methods: {
          password: settings.login_methods.password,
          google: {
            enabled: settings.login_methods.google.enabled,
            hosted_domains: settings.login_methods.google.hosted_domains.join(", "),
          },
          entra: {
            enabled: settings.login_methods.entra.enabled,
            tenant_ids: settings.login_methods.entra.tenant_ids.join(", "),
          },
          oidc: {
            enabled: settings.login_methods.oidc.enabled,
            title: settings.login_methods.oidc.title,
            issuer: settings.login_methods.oidc.issuer,
            client_id: settings.login_methods.oidc.client_id,
            client_secret: "",
            scopes: settings.login_methods.oidc.scopes.join(" "),
            email_claim: settings.login_methods.oidc.email_claim,
          },
        },
        ip_allowlist: {
          enabled: settings.ip_allowlist.enabled,
          networks: settings.ip_allowlist.networks.join("\n"),
        },
        allowed_domains: settings.allowed_domains.join(", "),
      });
    }
  }, [settings, reset]);

  const onSubmit = handleSubmit(async (data) => {
    const settings: ApiSecuritySettings = {
      ...(data as unknown as ApiSecuritySettings),
      login_methods: {
        password: data.login_methods.password,
        google: {
          enabled: data.login_methods.google.enabled,
          hosted_domains: parseDomains(data.login_methods.google.hosted_domains),
        },
        entra: {
          enabled: data.login_methods.entra.enabled,
          tenant_ids: parseTenantIds(data.login_methods.entra.tenant_ids),
        },
        oidc: {
          enabled: data.login_methods.oidc.enabled,
          title: data.login_methods.oidc.title.trim(),
          issuer: data.login_methods.oidc.issuer.trim(),
          client_id: data.login_methods.oidc.client_id.trim(),
          client_secret: data.login_methods.oidc.client_secret.trim() || undefined,
          scopes: parseScopes(data.login_methods.oidc.scopes),
          email_claim: data.login_methods.oidc.email_claim.trim() || "email",
        },
      },
      ip_allowlist: {
        enabled: data.ip_allowlist.enabled,
        networks: parseNetworks(data.ip_allowlist.networks),
      },
      allowed_domains: parseDomains(data.allowed_domains),
    };
    await securitySettingsUpdate.mutateAsync(settings);

    showInfo({ title: t("common:you_changes_were_saved") });

    analyticsService.trackEvent(
      settings.require_tfa
        ? analyticEvents.twoFactorAuthentication.settingsEnabled
        : analyticEvents.twoFactorAuthentication.settingsDisabled
    );
  });

  if (isLoading) {
    return (
      <Page>
        <Page.Header title={t("core:settings_security_title")} showBack={true} />
        <Page.Content>
          <Stack className={"px-16"} gap={"lg"}>
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Header title={t("core:settings_security_title")} showBack={true} />
      <Page.Content>
        <form onSubmit={onSubmit} className={"px-6 pb-(--gap-3xl)"}>
          <Stack className={"container-sm mx-auto"} gap={"lg"}>
            <Title header={t("core:settings_security_title")} size={"lg"} paddingTop />

            <Box>
              <Title
                header={t("core:authentication_methods")}
                size={"xs"}
                paddingTop
                paddingBottom
              />
              <Stack gap={"md"}>
                <Controller
                  control={control}
                  name="login_methods.password.enabled"
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      size={"sm"}
                      checked={value}
                      onChange={onChange}
                      title={t("core:login_method_password")}
                      description={t("core:login_method_password_description")}
                    />
                  )}
                />

                <Stack>
                  <Controller
                    control={control}
                    name="login_methods.google.enabled"
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size={"sm"}
                        checked={value}
                        onChange={onChange}
                        title={t("core:login_method_google")}
                        description={t("core:login_method_google_description")}
                      />
                    )}
                  />

                  {isGoogleEnabled && (
                    <Stack gap={"xs"} className={"pl-9"}>
                      <Input
                        label={t("core:google_workspace_domains")}
                        placeholder={"yourcompany.com"}
                        {...register("login_methods.google.hosted_domains")}
                        error={errors.login_methods?.google?.hosted_domains?.message}
                      />
                      <Text className={"text-secondary py-1 text-sm"}>
                        {t("core:google_workspace_domains_hint")}
                      </Text>
                    </Stack>
                  )}
                </Stack>

                <Stack>
                  {isEntraAvailable && (
                    <Controller
                      control={control}
                      name="login_methods.entra.enabled"
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size={"sm"}
                          checked={value}
                          onChange={onChange}
                          title={t("core:entra_sso")}
                          description={t("core:entra_sso_description")}
                        />
                      )}
                    />
                  )}

                  {isEntraAvailable && isEntraEnabled && (
                    <Stack gap={"xs"} className={"pl-9"}>
                      <Input
                        label={t("core:entra_sso_tenant_ids")}
                        placeholder={"00000000-0000-0000-0000-000000000000"}
                        {...register("login_methods.entra.tenant_ids")}
                        error={errors.login_methods?.entra?.tenant_ids?.message}
                      />
                      <Text className={"text-secondary text-sm"}>
                        {t("core:entra_sso_tenant_ids_hint")}
                      </Text>
                    </Stack>
                  )}
                </Stack>
                <Stack>
                  <Controller
                    control={control}
                    name="login_methods.oidc.enabled"
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size={"sm"}
                        checked={value}
                        onChange={onChange}
                        title={t("core:oidc_sso")}
                        description={t("core:oidc_sso_description")}
                      />
                    )}
                  />

                  {isOidcEnabled && (
                    <Stack gap={"xs"} className={"pl-9"}>
                      <Title
                        header={t("core:oidc_sign_in_appearance")}
                        size={"xs"}
                        paddingBottom
                        paddingTop
                        caption={t("core:oidc_title_hint")}
                      />
                      <Box variant={"border"}>
                        <Input
                          label={t("core:oidc_title")}
                          placeholder={"Acme SSO"}
                          {...register("login_methods.oidc.title")}
                          error={errors.login_methods?.oidc?.title?.message}
                        />
                      </Box>

                      <Title
                        header={t("core:oidc_provider_connection")}
                        size={"xs"}
                        paddingBottom
                        paddingTop
                      />
                      <Box variant={"border"}>
                        <Input
                          label={t("core:oidc_issuer")}
                          placeholder={"https://sso.yourcompany.com/realms/main"}
                          {...register("login_methods.oidc.issuer")}
                          error={errors.login_methods?.oidc?.issuer?.message}
                        />
                        <Stack className={"text-secondary text-sm py-2"}>
                          {t("core:oidc_issuer_hint")}
                        </Stack>
                        <Input
                          label={t("core:oidc_client_id")}
                          {...register("login_methods.oidc.client_id")}
                          error={errors.login_methods?.oidc?.client_id?.message}
                        />
                        <Input
                          type="password"
                          autoComplete="new-password"
                          label={t("core:oidc_client_secret")}
                          placeholder={
                            settings?.login_methods.oidc.client_secret_set ? "••••••••" : undefined
                          }
                          {...register("login_methods.oidc.client_secret")}
                          error={errors.login_methods?.oidc?.client_secret?.message}
                        />
                        {settings?.login_methods.oidc.client_secret_set && (
                          <Stack className={"text-secondary text-sm py-2"}>
                            {t("core:oidc_client_secret_hint")}
                          </Stack>
                        )}
                        {settings?.login_methods.oidc.redirect_uri && (
                          <>
                            <Input
                              label={t("core:oidc_redirect_uri")}
                              value={settings.login_methods.oidc.redirect_uri}
                              readOnly
                            />
                            <Stack className={"text-secondary text-sm py-2"}>
                              {t("core:oidc_redirect_uri_hint")}
                            </Stack>
                          </>
                        )}
                      </Box>

                      <Title
                        header={t("core:oidc_user_data")}
                        size={"xs"}
                        paddingBottom
                        paddingTop
                      />
                      <Box variant={"border"}>
                        <Input
                          label={t("core:oidc_email_claim")}
                          rightIcon={
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label={t("core:oidc_email_claim")}
                                  className={"text-secondary cursor-help"}
                                >
                                  <FontAwesomeIcon icon={"fa-light fa-circle-info"} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className={"max-w-sm"}>
                                {t("core:oidc_email_claim_tooltip")}
                              </TooltipContent>
                            </Tooltip>
                          }
                          placeholder={"email"}
                          {...register("login_methods.oidc.email_claim")}
                          error={errors.login_methods?.oidc?.email_claim?.message}
                        />
                        <Stack className={"text-secondary text-sm py-2"}>
                          {t("core:oidc_email_claim_hint")}
                        </Stack>
                        <Input
                          label={t("core:oidc_scopes")}
                          rightIcon={
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label={t("core:oidc_scopes")}
                                  className={"text-secondary cursor-help"}
                                >
                                  <FontAwesomeIcon icon={"fa-light fa-circle-info"} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className={"max-w-sm"}>
                                {t("core:oidc_scopes_tooltip")}
                              </TooltipContent>
                            </Tooltip>
                          }
                          placeholder={"openid email profile"}
                          {...register("login_methods.oidc.scopes")}
                          error={errors.login_methods?.oidc?.scopes?.message}
                        />
                        <Stack className={"text-secondary text-sm py-2"}>
                          {t("core:oidc_scopes_hint")}
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Stack>

                {errors.login_methods?.message && (
                  <Text className={"text-danger"}>{errors.login_methods.message}</Text>
                )}
              </Stack>
            </Box>

            <Box>
              <Title
                header={t("core:two_factor_authentication")}
                size={"xs"}
                paddingTop
                paddingBottom
              />
              <Controller
                control={control}
                name="require_tfa.enabled"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    size={"sm"}
                    checked={value}
                    onChange={onChange}
                    title={t("core:require_tfa")}
                  />
                )}
              />
            </Box>

            <Box>
              <Title header={t("core:ip_allowlist")} size={"xs"} paddingTop paddingBottom />
              <Stack>
                <Controller
                  control={control}
                  name="ip_allowlist.enabled"
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      size={"sm"}
                      checked={value}
                      onChange={onChange}
                      title={t("core:ip_allowlist_enabled")}
                      description={t("core:ip_allowlist_enabled_description")}
                    />
                  )}
                />

                {isIpAllowlistEnabled && (
                  <Stack gap={"xs"} className={"pl-9"}>
                    <Textarea
                      label={t("core:ip_allowlist_networks")}
                      placeholder={"203.0.113.0/24\n2001:db8::/32"}
                      {...register("ip_allowlist.networks")}
                      error={errors.ip_allowlist?.networks?.message}
                    />
                    <Text className={"text-secondary text-sm"}>
                      {t("core:ip_allowlist_networks_hint")}
                    </Text>
                    {settings?.current_ip && (
                      <Text className={"text-secondary text-sm"}>
                        {t("core:ip_allowlist_current_ip", { ip: settings.current_ip })}
                      </Text>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>

            <Box>
              <Title header={t("core:allowed_domains")} size={"xs"} paddingTop paddingBottom />
              <Stack gap={"xs"}>
                <Input
                  label={t("core:allowed_domains")}
                  placeholder={"yourcompany.com"}
                  {...register("allowed_domains")}
                  error={errors.allowed_domains?.message}
                />
                <Text className={"text-secondary"}>{t("core:allowed_domains_hint")}</Text>
              </Stack>
            </Box>

            <Box>
              <Title header={t("core:password_policy")} paddingTop size={"xs"} />
              <Stack>
                <Input
                  type="number"
                  label={t("core:min_length_value")}
                  {...register("password_min_length.value", {
                    setValueAs: (v) => (v === "" ? 0 : Number(v)),
                  })}
                  error={errors.password_min_length?.value?.message}
                />

                <Input
                  type="number"
                  label={t("core:expiry_days_value")}
                  {...register("password_expiry_days.value", {
                    setValueAs: (v) => (v === "" ? 0 : Number(v)),
                  })}
                  error={errors.password_expiry_days?.value?.message}
                />
              </Stack>
            </Box>

            <Stack gap={"md"} className={"pt-8"}>
              <ServerErrorField errors={errors} />
              <Button
                type="submit"
                size="lg"
                disabled={!isDirty || securitySettingsUpdate.isPending}
                isLoading={securitySettingsUpdate.isPending}
              >
                {t("common:save")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Page.Content>
    </Page>
  );
}
